import MentorStatusChip from 'components/Chips/MentorStatusChip';
import CopyText from 'components/CopyText';
import { ActionDrawer } from 'components/Drawer';
import CheckboxList from 'components/Drawer/CheckboxList';
import EllipsisText from 'components/EllipsisText';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import SettingsIcon from 'components/icons/Settings';
import List from 'components/list';
import Priority from 'components/list/Priority';
import { BoldTd, Td } from 'components/list/Td';
import { getOrderTh, Th } from 'components/list/Th';
import CONSIDERATIONS from 'enums/Considerations';
import EXPERTISE from 'enums/Expertise';
import FORMATIONS from 'enums/Formation';
import HOBBIES from 'enums/Hobby';
import { MENTOR_PRIORITY_LEVELS } from 'enums/PriorityLevels';
import PROFESSIONAL_SITUATION from 'enums/ProfessionalSituation';
import STUDY_LEVELS from 'enums/StudyLevels';
import React, { FC, useContext, useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setActiveColumns } from 'store/actions/admin/mentorLists';
import { StoreState } from 'store/reducers';
import { Action } from 'types/typeguards/reducerActions';
import { getLocaleDate } from 'utils/time';
import { MentorsContext, sortableColumns } from '..';

export const columnNames = [
  'firstName',
  'lastName',
  'priority',
  'email',
  'professionalSituation',
  'organism',
  'createdAt',
  'region',
  'postalCode',
  'trainingDate',
  'wantedMentorship',
  'assignedMentorship',
  'status',
  'jobSearch',
  'careerPlan',
  'studies',
  'entrepreneurship',
  'expertiseFields',
  'formations',
  'diploma',
  'consideration',
  'hobby',
  'lastInterview',
] as const;
export type ColumnNames = typeof columnNames[number];
const OrderTh = getOrderTh<typeof sortableColumns[number]>();

export type Columns = {
  [key in ColumnNames]: boolean;
};

export const initialActiveColumns: Columns = {
  firstName: true,
  lastName: true,
  priority: true,
  email: false,
  professionalSituation: true,
  organism: true,
  createdAt: true,
  region: false,
  postalCode: false,
  trainingDate: false,
  wantedMentorship: true,
  assignedMentorship: true,
  status: true,
  // Mentor form questions
  jobSearch: false,
  careerPlan: false,
  studies: false,
  entrepreneurship: false,
  expertiseFields: false,
  formations: false,
  diploma: false,
  consideration: false,
  hobby: false,
  lastInterview: false,
};

type Props = {
  activeColumns: Columns;
  setActiveColumns: (activeColumns: Columns) => void;
};
const MentorsList: FC<Props> = ({ activeColumns, setActiveColumns }) => {
  const { mentors, pages, page, setPage, loading, order, setOrder } =
    useContext(MentorsContext);

  const [settingsOpen, setSettingsOpen] = useState(false);
    useEffect(() => {
    const openMe = () => setSettingsOpen(true);
    window.addEventListener('open-mentor-settings', openMe);
    return () => window.removeEventListener('open-mentor-settings', openMe);
  }, []);

  const [selectedColumns, setSelectedColumns] = useState(activeColumns);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSelectedColumns({
      ...selectedColumns,
      [e.target.name]: e.target.checked,
    });
  };

  const columnNumber = useMemo(() => {
    // DISABLED (checkbox)
    // let c = 2
    let c = 1;
    Object.values(activeColumns).forEach((value) => {
      if (value) {
        c += 1;
      }
    });
    return c;
  }, [activeColumns]);

  return (
    <>
      <ActionDrawer
        open={settingsOpen}
        close={() => setSettingsOpen(false)}
        title="Colonnes mentors"
        subtitle="Activer les colonnes que vous souhaitez"
        cancel={() => {
          setSelectedColumns(activeColumns);
        }}
        apply={() => {
          setActiveColumns(selectedColumns);
        }}
      >
        <CheckboxList>
          <LabelledCheckbox
            label="Prioritaire"
            checked={selectedColumns.priority}
            name="priority"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Adresse email"
            checked={selectedColumns.email}
            name="email"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Situation actuelle"
            checked={selectedColumns.professionalSituation}
            name="professionalSituation"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Organisme"
            checked={selectedColumns.organism}
            name="organism"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date d'inscription"
            checked={selectedColumns.createdAt}
            name="createdAt"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Région"
            checked={selectedColumns.region}
            name="region"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Code postal"
            checked={selectedColumns.postalCode}
            name="postalCode"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date de formation"
            checked={selectedColumns.trainingDate}
            name="trainingDate"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Souhaités"
            checked={selectedColumns.wantedMentorship}
            name="wantedMentorship"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="En cours"
            checked={selectedColumns.assignedMentorship}
            name="assignedMentorship"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Statut du mentorat"
            checked={selectedColumns.status}
            name="status"
            onChange={handleChange}
          />
          {/* Mentor form questions */}
          <LabelledCheckbox
            label="Recherche d'emploi"
            checked={selectedColumns.jobSearch}
            name="jobSearch"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Carrière"
            checked={selectedColumns.careerPlan}
            name="careerPlan"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Études"
            checked={selectedColumns.studies}
            name="studies"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Entreprenariat"
            checked={selectedColumns.entrepreneurship}
            name="entrepreneurship"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Domaines de compétence"
            checked={selectedColumns.expertiseFields}
            name="expertiseFields"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Formations adaptées"
            checked={selectedColumns.formations}
            name="formations"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Niveau de diplôme"
            checked={selectedColumns.diploma}
            name="diploma"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Mode de vie"
            checked={selectedColumns.consideration}
            name="consideration"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Passe temps"
            checked={selectedColumns.hobby}
            name="hobby"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date du dernier entretien"
            checked={selectedColumns.lastInterview}
            name="lastInterview"
            onChange={handleChange}
          />
        </CheckboxList>
      </ActionDrawer>
      <List
        activeColumns={activeColumns}
        columnNumber={columnNumber}
        isEmpty={mentors?.length === 0}
        emptyText="Aucun mentor n'est disponible pour cette recherche"
        pages={pages}
        page={page}
        setPage={setPage}
        placeCheckbox
        loading={loading}
      >
        {/* 
				DISABLED
				<Th   name="checkbox"><Checkbox /></Th> */}
        <OrderTh name="firstName" order={order} setOrder={setOrder}>
          Prénom
        </OrderTh>
        <OrderTh name="lastName" order={order} setOrder={setOrder}>
          Nom
        </OrderTh>
        <OrderTh name="priority" order={order} setOrder={setOrder}>
          Prioritaire
        </OrderTh>
        <Th name="email">Email</Th>
        <OrderTh name="professionalSituation" order={order} setOrder={setOrder}>
          Situation actuelle
        </OrderTh>
        <OrderTh name="organism" order={order} setOrder={setOrder}>
          Organisme
        </OrderTh>
        <OrderTh name="createdAt" order={order} setOrder={setOrder}>
          Date d'inscription
        </OrderTh>
        <Th name="region">Région</Th>
        <Th name="postalCode">Code postal</Th>
        <Th name="trainingDate">Date de formation</Th>
        <Th name="wantedMentorship">Souhaités</Th>
        <Th name="assignedMentorship">En cours</Th>
        <OrderTh name="status" order={order} setOrder={setOrder}>
          Statut du mentorat
        </OrderTh>
        {/* Mentor form questions */}
        <Th name="jobSearch">Recherche d'emploi</Th>
        <Th name="careerPlan">Carrière</Th>
        <Th name="studies">Études</Th>
        <Th name="entrepreneurship">Entreprenariat</Th>
        <Th name="expertiseFields">Compétences</Th>
        <Th name="formations">Formations</Th>
        <Th name="diploma">Diplôme</Th>
        <Th name="consideration">Mode de vie</Th>
        <Th name="hobby">Passe temps</Th>
        <Th name="lastInterview">Dernier entretien</Th>
        <Th name="settings">
          <div
            style={{ stroke: 'var(--primary-700)', cursor: 'pointer' }}
            onClick={() => setSettingsOpen(true)}
          >
            <SettingsIcon />
          </div>{' '}
        </Th>
        {mentors?.map((mentor, idx) => (
          <React.Fragment key={idx}>
            {/* 
						DISABLED
						<Td   name="checkbox"><Checkbox /></Td> */}
            <BoldTd name="firstName">
              <Link to={`/admin/mentoring/mentors/${mentor.id}/`}>
                <EllipsisText text={mentor.firstName || ''} />
              </Link>
            </BoldTd>
            <BoldTd name="lastName">
              <Link to={`/admin/mentoring/mentors/${mentor.id}/`}>
                <EllipsisText text={mentor.lastName || ''} />
              </Link>
            </BoldTd>
            <Td name="priority">{mentor.priority ? <Priority /> : null}</Td>
            <Td name="email">
              <EllipsisText text={mentor.email || ''} />
              <CopyText text="" copiedText={mentor.email || ''} />
            </Td>
            <Td name="professionalSituation">
              <EllipsisText
                text={
                  mentor.professionalSituation
                    ? PROFESSIONAL_SITUATION[mentor.professionalSituation]
                    : ''
                }
              />
            </Td>
            <Td name="organism">
              <EllipsisText text={mentor.organism || ''} />
            </Td>
            <Td name="createdAt">
              {mentor.createdAt && getLocaleDate(mentor.createdAt)}
            </Td>
            <Td name="region">
              <EllipsisText text={mentor.region || ''} />
            </Td>
            <Td name="postalCode">{mentor.postalCode}</Td>
            <Td name="trainingDate">
              {mentor.MentorForm?.eventStartDate
                ? getLocaleDate(mentor.MentorForm.eventStartDate)
                : ''}
            </Td>
            <Td name="wantedMentorship">
              {mentor.MentorForm?.wantedMentorships}
            </Td>
            <Td name="assignedMentorship">{mentor.MentorMentorship?.length}</Td>
            <Td name="status">
              <MentorStatusChip status={mentor.mentorStatus!} />
            </Td>
            {/* Mentor form questions */}
            <Td name="jobSearch">
              {mentor.MentorForm?.jobSearch &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.jobSearch]}
            </Td>
            <Td name="careerPlan">
              {mentor.MentorForm?.careerPlan &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.careerPlan]}
            </Td>
            <Td name="studies">
              {mentor.MentorForm?.studies &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.studies]}
            </Td>
            <Td name="entrepreneurship">
              {mentor.MentorForm?.entrepreneurship &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.entrepreneurship]}
            </Td>
            <Td name="expertiseFields">
              <EllipsisText
                text={
                  mentor.MentorForm?.expertiseFields
                    ?.map(
                      (expertiseField) => EXPERTISE[expertiseField.expertise],
                    )
                    .join('; ') || ''
                }
              />
            </Td>
            <Td name="formations">
              <EllipsisText
                text={
                  mentor.MentorForm?.formations
                    ?.map(
                      (mentorFormation) =>
                        FORMATIONS[mentorFormation.formation],
                    )
                    .join('; ') || ''
                }
              />
            </Td>
            <Td name="diploma">
              <EllipsisText
                text={
                  mentor.MentorForm?.diploma
                    ?.map(
                      (mentorDiploma) => STUDY_LEVELS[mentorDiploma.diploma],
                    )
                    .join('; ') || ''
                }
              />
            </Td>
            <Td name="consideration">
              {mentor.MentorForm?.consideration &&
                CONSIDERATIONS[mentor.MentorForm.consideration]}
            </Td>
            <Td name="hobby">
              {mentor.MentorForm?.hobby && HOBBIES[mentor.MentorForm.hobby]}
            </Td>
            <Td name="lastInterview">
              {!!mentor.MentorMentorship?.length &&
                !!mentor.MentorMentorship[0].interviews?.length &&
                !!mentor.MentorMentorship[0].interviews[0].date &&
                getLocaleDate(mentor.MentorMentorship[0].interviews[0].date)}
            </Td>
            <Td name="settings"> </Td>
          </React.Fragment>
        ))}
      </List>
    </>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    activeColumns: state.admin.mentorLists.main.activeColumns,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setActiveColumns: (activeColumns: Columns) =>
      dispatch(setActiveColumns<Columns>('main', activeColumns)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MentorsList);
